import { Modal, Text } from '@components/1-atoms';
import * as utils from '@lib/utils';
import React, { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';


import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';


import { ChartList } from '@/components/3-organisms';
import { ChartDataPointProps } from '@/interfaces/chart.interface';
import importedStyles from '../styles';


// Isolated Child Component: BarSegment
const BarSegment: React.FC<any> = ({ targetWidthPct, progress, color, ...props }: any) => {
  const animatedStyle = useAnimatedStyle(() => {
    return {
      width: `${targetWidthPct * progress.value}%`,
    };
  });

  return (<Animated.View
    style={[importedStyles.barSegment, { backgroundColor: color }, animatedStyle]}
  />);
};

export type Props = {
  data: ChartDataPointProps[];
};

const HorizontalStackChart: React.FC<Props> = ({ data, ...props }) => {

  // Shared reanimated progress tracker
  const animationProgress = useSharedValue(0);

  const [selectedPointData, setSelectedPointData] = React.useState<ChartDataPointProps | null>(null);

  const selectedLabel = selectedPointData ? selectedPointData.label : "";
  const selectedCategories = selectedPointData ? selectedPointData.categories : [];

  React.useEffect(() => {
    const defaultIndex = data.length ? data.length - 1 : null;
    const seletedPoint = defaultIndex !== null ? data[defaultIndex] : null;
    setSelectedPointData(seletedPoint);
  }, [data]);

  // The periodic label modal
  const [isPeriodicLabelVisible, setPeriodicLabeVisiblility] = useState(false);
  const openModel = () => setPeriodicLabeVisiblility(true);
  const closeModel = () => setPeriodicLabeVisiblility(false);

  const onPressLabel = (point: ChartDataPointProps) => {
    setSelectedPointData(point);
    closeModel();
  }

  const [activeTab, setActiveTab] = useState('Expenses'); // 'Expenses' | 'Income'
  const [totalAmount, setTotalAmount] = React.useState(0);

  React.useEffect(() => {
    let amount = 0;
    if (activeTab === 'Expenses') {
      selectedCategories.forEach((category) => { amount += category.out; });
    } else if (activeTab === 'Income') {
      selectedCategories.forEach((category) => { amount += category.in; });
    }
    // Update the total expenses or income
    setTotalAmount(amount);
  }, [data, selectedCategories, activeTab]);

  React.useEffect(() => {
    animationProgress.value = 0; // Reset timeline
    animationProgress.value = withTiming(1, {
      duration: 1200 * 2,
      easing: Easing.out(Easing.cubic),
    });
  }, [animationProgress]);

  return (
    <View>

      {/* --- TAB CONTROLS & PERIOD --- */}
      <View style={importedStyles.controlsRow}>

        {/* --- TABS --- */}
        <View style={importedStyles.tabContainer}>
          {['Expenses', 'Income'].map((tab) => (
            <TouchableOpacity
              key={tab}
              style={[importedStyles.tab, (activeTab === tab && importedStyles.activeTab)]}
              onPress={() => setActiveTab(tab)}>
              <Text
                style={[
                  importedStyles.tabText,
                  (activeTab === tab && importedStyles.activeTabText),
                ]}>
                {tab}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* LABEL SELECTOR */}
        <View
          style={
            {
              borderRadius: 3,
              borderWidth: 3,
              borderColor: 'rgb(0, 90, 206)',
              borderStyle: 'solid',

            }
          }
        >
          <TouchableOpacity
            style={[
              importedStyles.labelTouchable,
              importedStyles.touchableLabelSelected,
              {
                position: "relative",

                width: 42 * 1.3,
                height: 24 * 1.3,

                borderWidth: 1,
                borderColor: '#FFF',
                borderStyle: 'solid',

              }
            ]}
            onPress={() => openModel()}
          >
            <Text style={[importedStyles.labelText, { fontSize: 11 * 1.3 }]}>{selectedLabel}</Text>
          </TouchableOpacity>
        </View>

      </View>

      {/* --- HEADER SUMMARY --- */}
      <View style={importedStyles.headerRow}>
        <View>
          <Text style={importedStyles.headerTitle}>
            $ {utils.printDecimalOf(totalAmount)}{' '} {activeTab === 'Expenses' ? 'spent' : 'earned'}
          </Text>
        </View>
      </View>

      {/* --- ANIMATED STACKED BAR --- */}
      <View style={importedStyles.barWrapper}>
        <View style={importedStyles.barContainer}>
          {selectedCategories.map((category) => {
            // The amount of this categorized item
            const itemAmount = (activeTab === 'Expenses') ? category.out : category.in;
            // Percent relative to total active volume
            const targetWidthPct = totalAmount > 0 ? (itemAmount / totalAmount) * 100 : 0;
            return <BarSegment
              key={(activeTab === 'Expenses') ? `${category.name}-out` : `${category.name}-in`}
              targetWidthPct={targetWidthPct}
              progress={animationProgress}
              color={category.color}
            />;
          })}
        </View>
      </View>

      {/* --- LEGEND / LIST --- */}
      <View style={importedStyles.listContainer}>
        {selectedCategories.map((category) => {
          // The amount of this categorized item
          const categoryAmount = (activeTab === 'Expenses') ? category.out : category.in;
          // Only list non-zero category legend amount
          if (!categoryAmount) {
            return null;
          }
          // Percent relative to total active volume
          const percentage =
            totalAmount > 0 ? ((categoryAmount / totalAmount) * 100).toFixed(1) : '0.0';
          return (
            <View key={category.name} style={importedStyles.listRow}>
              <View style={importedStyles.leftColumn}>
                <View
                  style={[importedStyles.bullet, { backgroundColor: category.color }]}
                />
                <Text style={importedStyles.categoryName}>{category.name}</Text>
              </View>
              <View style={importedStyles.rightColumn}>
                <Text style={importedStyles.categoryPercentage}>{percentage}%</Text>
                <Text style={importedStyles.categoryValue}>
                  $ {utils.printDecimalOf(categoryAmount)}
                </Text>
              </View>
            </View>
          );
        })}
      </View>


      {/* Model to select a different periodic label */}
      <Modal visible={isPeriodicLabelVisible} title={"Select a period"} onClose={closeModel}>
        <ChartList defaultValue={selectedPointData} data={data} onPress={onPressLabel} />
      </Modal>

    </View>
  );
};

export default HorizontalStackChart;